import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Filter, X as XIcon } from 'lucide-react';
import { EMP_TYPES, STATUS_OPTIONS } from './_shared';

/**
 * Search + 3-way combinable filter bar (Status × Job Type × Department).
 * All filters AND-intersect; the page applies them client-side after `load()`.
 */
export default function EmployeesFilterBar({
  query, setQuery,
  statusFilter, setStatusFilter,
  jobFilter, setJobFilter,
  deptFilter, setDeptFilter,
  deptOptions,
  filtered, total,
}) {
  const JOB_OPTIONS = [{ v: 'all', label: 'Any job type' }, ...EMP_TYPES.map(t => ({ v: t.v, label: t.label }))];
  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (jobFilter !== 'all' ? 1 : 0) +
    (deptFilter !== 'all' ? 1 : 0);
  const clearAllFilters = () => { setStatusFilter('all'); setJobFilter('all'); setDeptFilter('all'); };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="employee-search">
      <div className="flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" strokeWidth={1.8} />
        <input placeholder="Search by name, email, or department…" value={query}
               onChange={(e) => setQuery(e.target.value)}
               className="flex-1 outline-none bg-transparent text-sm"
               data-testid="employee-search-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 inline-flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5" /> Filters
        </span>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status"
                         className={`h-9 rounded-full text-xs px-3 w-auto min-w-[150px] ${statusFilter !== 'all' ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : ''}`}>
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(o => (
              <SelectItem key={o.v} value={o.v}>
                <span className="inline-flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${o.dot}`} />{o.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger data-testid="filter-job-type"
                         className={`h-9 rounded-full text-xs px-3 w-auto min-w-[150px] ${jobFilter !== 'all' ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : ''}`}>
            <SelectValue placeholder="Any job type" />
          </SelectTrigger>
          <SelectContent>
            {JOB_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger data-testid="filter-department"
                         className={`h-9 rounded-full text-xs px-3 w-auto min-w-[170px] ${deptFilter !== 'all' ? 'bg-[#3D4A8E] text-white border-[#3D4A8E]' : ''}`}>
            <SelectValue placeholder="Any department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any department</SelectItem>
            {deptOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            <SelectItem value="__none__">— No department —</SelectItem>
          </SelectContent>
        </Select>

        {activeFilterCount > 0 && (
          <button onClick={clearAllFilters} data-testid="clear-filters"
                  className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-red-50 transition-colors">
            <XIcon className="w-3 h-3" /> Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
          </button>
        )}

        <span className="ml-auto text-xs text-slate-500" data-testid="filter-result-count">
          <b className="text-slate-900">{filtered}</b> of {total} {filtered === 1 ? 'employee' : 'employees'}
        </span>
      </div>
    </div>
  );
}
